/** Textos y conversiones del tiempo estimado / planificado / real de una tarea (REQ-046). Lógica pura. */
import { fmtMinutes, type TaskTiming } from '../stores/workApi';

export type TimingTone = 'muted' | 'normal' | 'warn' | 'danger';

/** Línea de estado: «Sin estimación», «En ritmo · 22%», «Cerca del previsto · 85%», «Previsto alcanzado · 100%». */
export function timingStateText(t: Pick<TaskTiming, 'state' | 'consumedPct'>): { text: string; tone: TimingTone } {
  const pct = t.consumedPct !== null && t.consumedPct !== undefined ? ` · ${t.consumedPct}%` : '';
  switch (t.state) {
    case 'NO_ESTIMATE': return { text: 'Sin estimación', tone: 'muted' };
    case 'NEAR': return { text: `Cerca del previsto${pct}`, tone: 'warn' };
    case 'REACHED': return { text: `Previsto alcanzado${pct}`, tone: 'danger' };
    default: return { text: `En ritmo${pct}`, tone: 'normal' };
  }
}

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

/** «Desviación +40 min (+67%)» (solo con tarea cerrada y desviación relevante). */
export function deviationText(t: Pick<TaskTiming, 'relevantDeviation' | 'deviationMinutes' | 'deviationPct'>): string | null {
  if (!t.relevantDeviation || t.deviationMinutes === null || t.deviationPct === null) return null;
  const m = t.deviationMinutes;
  return `Desviación ${m > 0 ? '+' : m < 0 ? '-' : ''}${fmtMinutes(Math.abs(m))} (${signed(t.deviationPct)}%)`;
}

/** ISO (UTC) → valor para <input type="datetime-local"> en hora local. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Valor de <input type="datetime-local"> (hora local) → ISO UTC; vacío → null. */
export function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** «El fin planificado debe ser posterior al inicio» si procede. */
export function plannedRangeError(startLocal: string, endLocal: string): string | null {
  const s = fromLocalInput(startLocal);
  const e = fromLocalInput(endLocal);
  if (s && e && new Date(e) <= new Date(s)) return 'El fin planificado debe ser posterior al inicio';
  return null;
}
